import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import Modal from '../../modals/Modal';
// Components
import Box from '../shared/atoms/Box';
import Text from '../shared/atoms/Text';
import Link from '../shared/atoms/Link';
import Radio from '../shared/atoms/Radio';
import Heading from '../shared/atoms/Heading';
import Button from '../shared/atoms/Button';
import Plus16 from '../icons/Plus16';
import BillingAddressCard from '../customerArea/BillingAddresses/BillingAddressCard';
import BillingAddressForm from '../customerArea/BillingAddresses/BillingAddressForm';
// Services
import { getBillingAddresses } from '../../services/billingAddresses';

// The default billed party is not a BillingAddress (no id, not persisted — see
// CONTEXT.md), so it gets its own row rather than a fake BillingAddressCard.
// Visual vocabulary is kept in sync with BillingAddressCard on purpose.
const DefaultOptionContainer = styled(Box)`
  border: ${(p) =>
    p.selected
      ? `2px solid ${themeGet('colors.blue.electric')(p)}`
      : `1px solid ${themeGet('colors.gray.light')(p)}`};
  background: ${themeGet('colors.white')};
  transition: all 0.25s ease;
  ${css`
    cursor: pointer;
    &:hover {
      border-color: ${themeGet('colors.blue.electric')};
    }
  `}
`;

const RadioBox = styled(Box)`
  width: 20px;
  height: 20px;
  position: relative;
`;

const BillingAddressPicker = ({
  open,
  onRequestClose,
  clientId,
  selectedId,
  defaultOption,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCreating(false);
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const rep = await getBillingAddresses(clientId);
        if (!cancelled) setAddresses(Array.isArray(rep) ? rep : []);
      } catch (err) {
        console.error('error getBillingAddresses', err);
        if (!cancelled) setAddresses([]);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  const handleSelect = (address) => {
    onSelect(address);
    onRequestClose();
  };

  const handleCreated = (address) => {
    onSelect(address);
    onRequestClose();
  };

  return (
    <Modal
      zIndex="1003"
      width="small"
      hasCloseAction={true}
      open={open}
      onRequestClose={onRequestClose}>
      <Heading mb={4} level={5}>
        {t('repairconfirmation.billing.picker.title')}
      </Heading>
      {creating ? (
        <>
          <BillingAddressForm clientId={clientId} onSuccess={handleCreated} />
          <Box alignItems="center" mt={3}>
            <Link color="blue.electric" onClick={() => setCreating(false)}>
              {t('button.cancel')}
            </Link>
          </Box>
        </>
      ) : (
        <>
          {defaultOption && (
            <DefaultOptionContainer
              borderRadius="xs"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              selected={!selectedId}
              onClick={() => handleSelect(null)}
              p={4}
              mb={4}>
              <Box width="100%">
                <Box
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%">
                  <Box>
                    <Text fontWeight="bold" color="black" mb={1}>
                      {defaultOption.name}
                    </Text>
                    {defaultOption.lines.map((line, index) => (
                      // Free-text company lines can repeat — index-keyed on
                      // purpose; the list is static within a render.
                      // eslint-disable-next-line react/no-array-index-key
                      <Text
                        key={`${index}-${line}`}
                        variant="small"
                        color="gray.darkest">
                        {line}
                      </Text>
                    ))}
                    {defaultOption.noteKey && (
                      <Text variant="small" color="blue.electric" mt={1}>
                        {t(defaultOption.noteKey)}
                      </Text>
                    )}
                  </Box>
                  <RadioBox>{!selectedId && <Radio checked />}</RadioBox>
                </Box>
              </Box>
            </DefaultOptionContainer>
          )}
          {!loading && !addresses.length && !defaultOption && (
            <Text mb={4}>{t('billingaddresses.empty.text')}</Text>
          )}
          {addresses.map((address) => (
            <BillingAddressCard
              key={address.id}
              address={address}
              selectable
              selected={address.id === selectedId}
              onSelect={handleSelect}
            />
          ))}
          <Button
            before={<Plus16 />}
            variant="outlinedBlack"
            onClick={() => setCreating(true)}>
            {t('repairconfirmation.billing.picker.new')}
          </Button>
        </>
      )}
    </Modal>
  );
};

export default BillingAddressPicker;
